<script setup lang="ts">
   import { computed, watch } from 'vue';
   import type {
      TournamentEntryFieldTeamRoster,
      TournamentEntryRosterColumn,
      TournamentEntryRosterRow,
   } from '~/types/types';

   const props = defineProps<{
      field: TournamentEntryFieldTeamRoster;
      modelValue: TournamentEntryRosterRow[] | undefined;
      errors?: Record<string, string>;
      disabled?: boolean;
   }>();

   const emit = defineEmits<{
      'update:modelValue': [value: TournamentEntryRosterRow[]];
   }>();

   const rows = computed<TournamentEntryRosterRow[]>(() => props.modelValue ?? []);

   // Fallback for responses that predate the API's `columns` descriptor. The
   // live API always sends `columns`; this is belt-and-suspenders for stale
   // caches so required-ness never regresses to "all required".
   const DEFAULT_COLUMNS: TournamentEntryRosterColumn[] = [
      { key: 'first_name', label: 'First name', required: true, maxlength: 100 },
      { key: 'last_name', label: 'Last name', required: false, maxlength: 100 },
      { key: 'handicap', label: 'Handicap', required: false, maxlength: 10 },
   ];

   const columns = computed<TournamentEntryRosterColumn[]>(() =>
      props.field.columns?.length ? props.field.columns : DEFAULT_COLUMNS
   );

   const ensureInitialRows = () => {
      if (rows.value.length > 0) return;
      const initial: TournamentEntryRosterRow[] = Array.from(
         { length: props.field.default_player_count },
         () => ({ first_name: '', last_name: '', handicap: '' })
      );
      emit('update:modelValue', initial);
   };

   watch(
      () => props.field,
      () => ensureInitialRows(),
      { immediate: true }
   );

   const canAdd = computed(() => rows.value.length < props.field.max_players);
   const canRemove = computed(() => rows.value.length > props.field.min_players);

   const updateRow = (index: number, key: keyof TournamentEntryRosterRow, value: string) => {
      const next = rows.value.map((row, i) => (i === index ? { ...row, [key]: value } : row));
      emit('update:modelValue', next);
   };

   const addRow = () => {
      if (!canAdd.value) return;
      emit('update:modelValue', [
         ...rows.value,
         { first_name: '', last_name: '', handicap: '' },
      ]);
   };

   const removeRow = (index: number) => {
      if (!canRemove.value) return;
      const next = rows.value.filter((_, i) => i !== index);
      emit('update:modelValue', next);
   };

   const errorFor = (index: number, key: keyof TournamentEntryRosterRow) =>
      props.errors?.[`${index}.${key}`];

   const cellInputClass =
      'w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 disabled:bg-gray-100 disabled:text-gray-500';
</script>

<template>
   <fieldset class="mb-4 rounded-lg border border-gray-200 p-4">
      <legend class="px-2 text-base font-semibold text-gray-900">
         {{ field.label }}
         <span v-if="field.required" class="ml-0.5 text-red-700" aria-hidden="true">*</span>
         <small class="ml-2 font-normal text-gray-500">
            ({{ field.min_players }}–{{ field.max_players }} players)
         </small>
      </legend>

      <div class="mt-3 flex flex-col gap-3">
         <div
            v-for="(row, index) in rows"
            :key="index"
            class="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_1fr_6rem_auto]">
            <div v-for="col in columns" :key="col.key" class="flex flex-col gap-1">
               <label :for="`roster-${col.key}-${index}`" class="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {{ col.label }}
                  <span v-if="col.required" class="text-red-700" aria-hidden="true">*</span>
               </label>
               <input
                  :id="`roster-${col.key}-${index}`"
                  type="text"
                  :value="row[col.key]"
                  :required="col.required"
                  :maxlength="col.maxlength"
                  :disabled="disabled"
                  :class="[cellInputClass, errorFor(index, col.key) ? 'border-red-700' : 'border-gray-300 focus:border-cyan-500']"
                  @input="updateRow(index, col.key, ($event.target as HTMLInputElement).value)" />
               <p v-if="errorFor(index, col.key)" class="text-xs text-red-700">
                  {{ errorFor(index, col.key) }}
               </p>
            </div>

            <button
               v-if="canRemove"
               type="button"
               class="rounded-md border border-red-200 px-3 py-2 text-xs text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
               :disabled="disabled"
               @click="removeRow(index)">
               Remove
            </button>
         </div>
      </div>

      <button
         v-if="canAdd"
         type="button"
         class="mt-3 rounded-md border border-dashed border-gray-400 bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
         :disabled="disabled"
         @click="addRow">
         + Add Player
      </button>
   </fieldset>
</template>
